import React from "react";
import { motion } from "framer-motion";
import { HelpCircle, BookOpen, GraduationCap, Settings, BarChart3, Users, Calendar, FileText } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";

const HelpCenter = () => {
  return (
    <div className="container py-8 max-w-5xl mx-auto">
      <div className="mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold tracking-tight text-nalc-dark dark:text-white mb-4">
            Help Center
          </h1>
          <p className="text-muted-foreground">
            Learn how to use the Noor Ali Learning Center Student Program & Fee Tracker
          </p>
        </motion.div>

        <Tabs defaultValue="getting-started" className="space-y-6">
          <TabsList className="w-full justify-start overflow-auto py-2">
            <TabsTrigger value="getting-started">Getting Started</TabsTrigger>
            <TabsTrigger value="students">Students</TabsTrigger>
            <TabsTrigger value="exams">Exams</TabsTrigger>
            <TabsTrigger value="teachers">Teachers</TabsTrigger>
            <TabsTrigger value="timetable">Timetable</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Getting Started Section */}
          <TabsContent value="getting-started" className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-nalc-teal" />
                    Welcome to NALC Student Program & Fee Tracker
                  </CardTitle>
                  <CardDescription>
                    An introduction to the application and its features
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    The NALC Student Program & Fee Tracker is a comprehensive application designed to help educational
                    institutions manage their students, teachers, programs, and finances efficiently. This application
                    provides tools for tracking student enrollments, monitoring fee payments, managing teacher schedules,
                    and generating detailed reports.
                  </p>
                  
                  <div className="bg-muted p-4 rounded-md">
                    <h3 className="font-medium mb-2">Key Features:</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Student management with program enrollment tracking</li>
                      <li>Teacher profiles with qualification and attendance monitoring</li>
                      <li>Interactive timetable and scheduling system</li>
                      <li>Fee tracking and payment processing</li>
                      <li>Comprehensive reporting and analytics</li>
                      <li>Certificate management and generation</li>
                      <li>Exam results management and tracking</li>
                      <li>Data backup and restoration</li>
                    </ul>
                  </div>
                  
                  <div className="mt-4">
                    <h3 className="font-medium mb-2">Navigation:</h3>
                    <p>
                      Use the sidebar on the left to navigate between different sections of the application.
                      Each section is designed to handle specific aspects of your educational institution's management.
                    </p>
                  </div>

                  <div className="bg-muted p-4 rounded-md">
                    <h3 className="font-medium mb-2">Data Storage:</h3>
                    <p>
                      When installed, the application creates a dedicated storage area on your device:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                      <li>All data (students, programs, exams, etc.) is stored locally on your device</li>
                      <li>On desktop/PC: The app creates its own data folder in your system's application data area</li>
                      <li>On mobile: Data is stored in a protected app-specific storage location</li>
                      <li>All functionality works offline without requiring internet connection</li>
                      <li>Backup files can be exported and stored in your preferred location</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-nalc-teal" />
                    Dashboard Overview
                  </CardTitle>
                  <CardDescription>
                    Understanding your dashboard and key metrics
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    The dashboard is your central hub for monitoring the overall status of your educational institution.
                    It provides at-a-glance insights into important metrics and recent activities.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-1">Statistics Cards</h3>
                      <p className="text-sm text-muted-foreground">
                        The top section displays key statistics including total students, new enrollments, active programs,
                        and revenue metrics. Click on any card to navigate to the detailed view.
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="font-medium mb-1">Charts and Graphs</h3>
                      <p className="text-sm text-muted-foreground">
                        Visual representations of enrollment trends and program distribution help you understand
                        patterns and make data-driven decisions.
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="font-medium mb-1">Quick Actions</h3>
                      <p className="text-sm text-muted-foreground">
                        Frequently used actions are accessible directly from the dashboard, saving you time
                        when performing common tasks.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Students Section */}
          <TabsContent value="students" className="space-y-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-nalc-teal" />
                    Managing Students
                  </CardTitle>
                  <CardDescription>
                    Adding, editing, and monitoring student information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-medium">Adding a New Student</h3>
                    <ol className="list-decimal pl-5 space-y-2">
                      <li>Navigate to the Students page from the sidebar.</li>
                      <li>Click the "Add New Student" button in the top right corner.</li>
                      <li>Fill in the student's basic information in the form.</li>
                      <li>Add program enrollments by selecting from available programs.</li>
                      <li>Set fee details and payment status for each program.</li>
                      <li>Upload any necessary documents or profile images.</li>
                      <li>Click "Save" to create the student record.</li>
                    </ol>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Viewing Student Details</h3>
                    <p>
                      From the Students list, click on any student card to view their detailed profile.
                      The student details page includes:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                      <li>Personal information and contact details</li>
                      <li>Enrolled programs and their status</li>
                      <li>Fee payment history and outstanding balances</li>
                      <li>Exam results and academic performance</li>
                      <li>Attendance records and program progress</li>
                      <li>Certification status and history</li>
                    </ul>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Managing Student Payments</h3>
                    <p>
                      To process a payment for a student:
                    </p>
                    <ol className="list-decimal pl-5 space-y-1 mt-2">
                      <li>Navigate to the student's detail page.</li>
                      <li>Click on the "Process Payment" button.</li>
                      <li>Select the program for which payment is being made.</li>
                      <li>Enter the payment amount and method.</li>
                      <li>Add any notes or reference numbers.</li>
                      <li>Click "Complete Payment" to record the transaction.</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Exams Section - New Section */}
          <TabsContent value="exams" className="space-y-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-nalc-teal" />
                    Exam Management
                  </CardTitle>
                  <CardDescription>
                    Recording, tracking, and analyzing student exam results
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-medium">Adding Exam Results</h3>
                    <ol className="list-decimal pl-5 space-y-1">
                      <li>Navigate to the student's profile from the Students list.</li>
                      <li>Click on the "Exams" tab within the student profile.</li>
                      <li>Click the "Add Result" button to create a new exam entry.</li>
                      <li>Enter the exam details including subject, type, and date.</li>
                      <li>Input the obtained score and maximum possible score.</li>
                      <li>Select the program related to this exam (if applicable).</li>
                      <li>Add any additional notes or comments if needed.</li>
                      <li>Click "Save" to record the exam result.</li>
                    </ol>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Managing Exam Results</h3>
                    <p>
                      The Exams tab displays a comprehensive list of all recorded exams for the student:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                      <li>View exam scores represented as both raw scores and percentages</li>
                      <li>Color-coded scores indicate performance levels (red for failing, yellow for average, green for good)</li>
                      <li>Edit any existing exam entry by clicking the edit icon</li>
                      <li>Delete exam entries if they were recorded incorrectly</li>
                      <li>Sort and filter exams by subject, date, score, or exam type</li>
                    </ul>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Printing Exam Reports</h3>
                    <ol className="list-decimal pl-5 space-y-1 mt-2">
                      <li>From the Exams tab on a student's profile, locate the "Print Report" button.</li>
                      <li>Click on the button to generate a PDF report containing the student's exam results.</li>
                      <li>The system will create a professional PDF document with:</li>
                      <ul className="list-disc pl-5 space-y-1 ml-5 mt-1">
                        <li>Student details and institutional information</li>
                        <li>Exam scores with performance indicators</li>
                        <li>Statistical analysis of the student's performance</li>
                      </ul>
                      <li>Save the PDF to your device or print a physical copy.</li>
                    </ol>
                  </div>

                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Exam Statistics and Analytics</h3>
                    <p>
                      The application provides helpful analytics to understand student performance:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                      <li>Average scores across different subjects and exam types</li>
                      <li>Performance trends over time with visual charts</li>
                      <li>Comparison with class or program averages (where available)</li>
                      <li>Identification of strengths and areas for improvement</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Teachers Section */}
          <TabsContent value="teachers" className="space-y-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-nalc-teal" />
                    Teacher Management
                  </CardTitle>
                  <CardDescription>
                    Managing teacher profiles, assignments, and payments
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-medium">Adding a New Teacher</h3>
                    <ol className="list-decimal pl-5 space-y-2">
                      <li>Navigate to the Teachers page from the sidebar.</li>
                      <li>Click the "Add New Teacher" button.</li>
                      <li>Fill in personal and contact information.</li>
                      <li>Add qualifications, experience, and specializations.</li>
                      <li>Set salary information and payment details.</li>
                      <li>Upload supporting documents and profile image.</li>
                      <li>Click "Save" to create the teacher record.</li>
                    </ol>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Teacher Assignments and Scheduling</h3>
                    <p>
                      To assign classes and manage a teacher's schedule:
                    </p>
                    <ol className="list-decimal pl-5 space-y-1 mt-2">
                      <li>Go to the Timetable section from the sidebar.</li>
                      <li>Click "Create New Class" or select an existing time slot.</li>
                      <li>Select the teacher from the dropdown list.</li>
                      <li>Assign subjects, programs, and student groups.</li>
                      <li>Set the recurring schedule or one-time class details.</li>
                      <li>Save the assignment to update the teacher's schedule.</li>
                    </ol>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Processing Teacher Payments</h3>
                    <p>
                      To process salary payments for teachers:
                    </p>
                    <ol className="list-decimal pl-5 space-y-1 mt-2">
                      <li>Navigate to the teacher's profile page.</li>
                      <li>Click on the "Process Payment" tab or button.</li>
                      <li>Select the payment period (month/date range).</li>
                      <li>Review attendance and performance metrics.</li>
                      <li>Enter payment amount, including any allowances or deductions.</li>
                      <li>Select payment method and add reference details.</li>
                      <li>Click "Complete Payment" to record the transaction.</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Timetable Section */}
          <TabsContent value="timetable" className="space-y-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-nalc-teal" />
                    Timetable Management
                  </CardTitle>
                  <CardDescription>
                    Scheduling classes and managing the academic calendar
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-medium">Creating a New Class Schedule</h3>
                    <ol className="list-decimal pl-5 space-y-1">
                      <li>Navigate to the Timetable section from the sidebar.</li>
                      <li>Click on "New Class" or select an empty time slot.</li>
                      <li>Select the teacher, subject, and class group.</li>
                      <li>Set the time, duration, and recurrence pattern.</li>
                      <li>Add any notes or special instructions.</li>
                      <li>Click "Save" to create the schedule.</li>
                    </ol>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Managing Class Attendance</h3>
                    <p>
                      To record and monitor attendance for scheduled classes:
                    </p>
                    <ol className="list-decimal pl-5 space-y-1 mt-2">
                      <li>Go to the Attendance section from the sidebar.</li>
                      <li>Select the date and class from the calendar view.</li>
                      <li>Mark each student as present, absent, or late.</li>
                      <li>Add any comments or notes if necessary.</li>
                      <li>Click "Save Attendance" to record the data.</li>
                      <li>Review attendance patterns and statistics from the reports section.</li>
                    </ol>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Handling Schedule Conflicts</h3>
                    <p>
                      When a scheduling conflict occurs:
                    </p>
                    <ol className="list-decimal pl-5 space-y-1 mt-2">
                      <li>The system will automatically highlight conflicting schedules.</li>
                      <li>Navigate to the Timetable Alerts section to view all conflicts.</li>
                      <li>Click on a conflict to see detailed information.</li>
                      <li>Choose to reschedule one of the conflicting classes.</li>
                      <li>Alternatively, assign a substitute teacher if available.</li>
                      <li>Update the schedule and confirm the changes.</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Reports Section */}
          <TabsContent value="reports" className="space-y-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-nalc-teal" />
                    Generating Reports
                  </CardTitle>
                  <CardDescription>
                    Analytics, statistics, and data export options
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-medium">Accessing Different Report Types</h3>
                    <p>
                      The Reports section provides various types of analytics:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                      <li><strong>Enrollment Reports:</strong> Track new enrollments, active students, and program distribution.</li>
                      <li><strong>Financial Reports:</strong> Monitor revenue, outstanding payments, and financial forecasts.</li>
                      <li><strong>Academic Reports:</strong> Analyze exam results, attendance patterns, and student performance.</li>
                      <li><strong>Teacher Reports:</strong> Evaluate teaching hours, student feedback, and qualification metrics.</li>
                      <li><strong>Administrative Reports:</strong> Generate custom reports for institutional metrics.</li>
                    </ul>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Generating and Exporting Reports</h3>
                    <ol className="list-decimal pl-5 space-y-1">
                      <li>Navigate to the Reports section from the sidebar.</li>
                      <li>Select the type of report you want to generate.</li>
                      <li>Apply filters for date range, programs, teachers, or other criteria.</li>
                      <li>Click "Generate Report" to view the results.</li>
                      <li>Use the export options to download as Excel, PDF, or CSV format.</li>
                      <li>Alternatively, print the report directly from the application.</li>
                    </ol>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Creating Custom Dashboards</h3>
                    <p>
                      To create personalized analytics views:
                    </p>
                    <ol className="list-decimal pl-5 space-y-1 mt-2">
                      <li>From the Reports section, click "Custom Dashboard."</li>
                      <li>Select the metrics and charts you want to include.</li>
                      <li>Arrange the layout according to your preference.</li>
                      <li>Save the dashboard with a descriptive name.</li>
                      <li>Access your custom dashboards anytime from the Reports section.</li>
                      <li>Share dashboards with other administrators if needed.</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Settings Section */}
          <TabsContent value="settings" className="space-y-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5 text-nalc-teal" />
                    Application Settings
                  </CardTitle>
                  <CardDescription>
                    Customizing your experience and managing system preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-medium">Account Settings</h3>
                    <p>
                      Manage your user account and preferences:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                      <li>Update your profile information and contact details.</li>
                      <li>Change your password and security settings.</li>
                      <li>Manage notification preferences and alerts.</li>
                      <li>Toggle between light and dark theme for the interface.</li>
                    </ul>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Data Backup and Security</h3>
                    <p>
                      To manage your application data:
                    </p>
                    <ol className="list-decimal pl-5 space-y-1 mt-2">
                      <li>Navigate to the Settings page from the sidebar.</li>
                      <li>Scroll to the "Backup Management" section.</li>
                      <li>Click "Create Backup" to save your current data.</li>
                      <li>Download the backup file for safekeeping.</li>
                      <li>To restore data, use the "Import Backup" option.</li>
                      <li>Set up automatic backups to protect your information.</li>
                    </ol>
                    <div className="bg-muted p-4 rounded-md mt-2">
                      <h4 className="font-medium mb-1">About Backup Files:</h4>
                      <p className="text-sm">
                        Backup files contain all application data including students, programs, 
                        exams, and settings. These files are stored in your device's downloads 
                        folder by default, but you can move them to any secure location.
                        Regular backups are recommended to prevent data loss.
                      </p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    <h3 className="font-medium">Password Protection</h3>
                    <p>
                      To secure your application with password protection:
                    </p>
                    <ol className="list-decimal pl-5 space-y-1 mt-2">
                      <li>Go to the Settings page and find "Password Protection."</li>
                      <li>Enable the feature by toggling the switch.</li>
                      <li>Create a strong password for unlocking the application.</li>
                      <li>Set options for automatic locking and timeout duration.</li>
                      <li>Save your settings to activate password protection.</li>
                      <li>Test the lock screen to ensure proper functionality.</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle>Need Further Assistance?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="mb-4">
                    If you need additional help or have specific questions that aren't covered in this documentation, 
                    please contact the developer directly using the information provided in the About Us page.
                  </p>
                  <div className="bg-muted p-4 rounded-md">
                    <h4 className="font-medium mb-2">Developer Contact:</h4>
                    <p className="text-sm">
                      Shukrullah Samadi<br />
                      Email: www.mrdesigner.af@gmail.com<br />
                      WhatsApp: +93 784604196<br />
                      Website: https://mrshukraan.netlify.app
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HelpCenter;
